import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, User, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const UserRegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(true);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      setLoading(false);
      return;
    }

    const result = await register(
      formData.firstName,
      formData.lastName,
      formData.email,
      formData.password
    );
    
    if (result.success) {
      if (result.autoLogin) {
        // User was auto-logged in, redirect to home
        navigate("/");
      } else {
        // Show appropriate success message
        setNeedsVerification(result.needsVerification);
        setRegistrationSuccess(true);
      }
    } else {
      setError("Error al registrar usuario. Verifica que el email no esté en uso.");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="mb-8 flex items-center text-white hover:text-purple-200 transition-colors duration-300"
        >
          <ArrowLeft size={20} className="mr-2" />
          Volver al inicio
        </button>

        {/* Registration Form or Success Message */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {!registrationSuccess ? (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <User size={32} className="text-purple-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  Crear Cuenta
                </h1>
                <p className="text-gray-600">
                  Únete a Libro de Cielo
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {loading ? "Creando cuenta..." : "Crear cuenta"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿Ya tienes una cuenta?{" "}
              <Link
                to="/user-login"
                className="font-medium text-purple-600 hover:text-purple-500 transition-colors duration-300"
              >
                Iniciar sesión
              </Link>
            </p>
          </div>
            </>
          ) : (
            /* Success Message */
            <div className="text-center">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                needsVerification ? "bg-green-100" : "bg-purple-100"
              }`}>
                {needsVerification ? (
                  <Mail size={32} className="text-green-600" />
                ) : (
                  <User size={32} className="text-purple-600" />
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                ¡Registro Exitoso!
              </h1>
              
              {needsVerification ? (
                <>
                  <p className="text-gray-600 mb-6">
                    Hemos enviado un enlace de verificación a <strong>{formData.email}</strong>
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-blue-800 mb-2">Próximos pasos:</h3>
                    <ol className="text-sm text-blue-700 space-y-1 text-left">
                      <li>1. Revisa tu bandeja de entrada</li>
                      <li>2. Haz clic en el enlace de verificación</li>
                      <li>3. Inicia sesión en tu cuenta</li>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Tu cuenta ha sido creada exitosamente. Hemos enviado un email de bienvenida a <strong>{formData.email}</strong>
                  </p>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-purple-800 mb-2">¡Ya puedes comenzar!</h3>
                    <p className="text-sm text-purple-700">
                      Tu cuenta está lista para usar. Puedes iniciar sesión inmediatamente.
                    </p>
                  </div>
                </>
              )}
              
              <div className="space-y-4">
                <button
                  onClick={() => navigate("/user-login")}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-medium"
                >
                  {needsVerification ? "Ir a Iniciar Sesión" : "Iniciar Sesión"}
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 font-medium"
                >
                  Volver al Inicio
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRegisterPage;